import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, RotateCw, Check, X, Maximize2, Square, Circle, RectangleHorizontal, Scan } from 'lucide-react';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const rotW = image.width * cos + image.height * sin;
  const rotH = image.width * sin + image.height * cos;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.translate(-pixelCrop.x - pixelCrop.width / 2, -pixelCrop.y - pixelCrop.height / 2);
  ctx.translate(rotW / 2, rotH / 2);
  ctx.rotate(radians);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  ctx.restore();

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

const ASPECT_PRESETS = [
  { label: 'Square', value: 1, icon: Square },
  { label: '4:3', value: 4 / 3, icon: RectangleHorizontal },
  { label: '3:2', value: 3 / 2, icon: RectangleHorizontal },
  { label: 'Free', value: null, icon: Scan },
];

export const ImageCropper = ({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1,
  shape = 'rect',
  allowAspectChange = false,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentAspect, setCurrentAspect] = useState(aspectRatio);
  const [currentShape, setCurrentShape] = useState(shape);
  const [mediaSize, setMediaSize] = useState(null);

  // Reset state when dialog opens with new image
  useEffect(() => {
    if (open && imageSrc) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCurrentAspect(aspectRatio);
      setCurrentShape(shape);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc, aspectRatio, shape]);

  const onCropChange = useCallback((location) => setCrop(location), []);
  const onZoomChange = useCallback((z) => setZoom(z), []);

  const handleCropAreaComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const onMediaLoaded = useCallback((mediaSize) => {
    setMediaSize(mediaSize);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
    onCropComplete(croppedBlob);
    onClose();
  };

  const handleFitToView = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const handleZoomOut = () => {
    setZoom(0.5);
    setCrop({ x: 0, y: 0 });
  };

  const handleReset = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setRotation(0);
  };

  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleAspectChange = (preset) => {
    if (preset.value === null) {
      setCurrentAspect(undefined);
      setCurrentShape('rect');
    } else {
      setCurrentAspect(preset.value);
      setCurrentShape('rect');
    }
  };

  const toggleShape = () => {
    setCurrentShape(s => s === 'round' ? 'rect' : 'round');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] p-0 overflow-hidden" data-testid="image-cropper-dialog">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="text-base font-semibold">Adjust Your Photo</DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drag to reposition &middot; Pinch or scroll to zoom &middot; Use controls below to fine-tune
          </p>
        </DialogHeader>

        {/* Crop Area - larger for better UX */}
        <div className="relative w-full h-80 sm:h-[420px] bg-neutral-950 mx-0">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={currentAspect}
              cropShape={currentShape}
              minZoom={0.3}
              maxZoom={5}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={handleCropAreaComplete}
              onMediaLoaded={onMediaLoaded}
              showGrid={true}
              objectFit="contain"
              restrictPosition={false}
              style={{
                containerStyle: { borderRadius: 0 },
                cropAreaStyle: {
                  border: '2px solid rgba(255,255,255,0.8)',
                  color: 'rgba(0,0,0,0.55)',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                }
              }}
            />
          )}
        </div>

        {/* Controls Panel */}
        <div className="px-5 py-4 space-y-4 bg-background">
          {/* Zoom Control */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground">Zoom</label>
              <span className="text-xs tabular-nums text-muted-foreground">{Math.round(zoom * 100)}%</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
                onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}>
                <ZoomOut className="w-3.5 h-3.5" />
              </Button>
              <Slider
                value={[zoom]}
                onValueChange={([v]) => setZoom(v)}
                min={0.3}
                max={5}
                step={0.02}
                className="flex-1"
                data-testid="crop-zoom-slider"
              />
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0"
                onClick={() => setZoom(z => Math.min(5, z + 0.1))}>
                <ZoomIn className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Quick Actions + Aspect Ratios */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleFitToView}
                data-testid="crop-fit-btn">
                <Maximize2 className="w-3.5 h-3.5 mr-1" />Fit
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleZoomOut}>
                <ZoomOut className="w-3.5 h-3.5 mr-1" />Show More
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleRotate}
                data-testid="crop-rotate-btn">
                <RotateCw className="w-3.5 h-3.5 mr-1" />Rotate
              </Button>
              {shape === 'round' && (
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={toggleShape}>
                  {currentShape === 'round' ? (
                    <><Square className="w-3.5 h-3.5 mr-1" />Square</>
                  ) : (
                    <><Circle className="w-3.5 h-3.5 mr-1" />Circle</>
                  )}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleReset}>
                Reset
              </Button>
            </div>

            {/* Aspect Ratio presets (only for non-profile photos) */}
            {allowAspectChange && (
              <div className="flex gap-1 bg-muted/50 rounded-md p-0.5">
                {ASPECT_PRESETS.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => handleAspectChange(preset)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                      (preset.value === null && currentAspect === undefined) ||
                      (preset.value !== null && currentAspect === preset.value)
                        ? 'bg-background shadow-sm text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`aspect-${preset.label.toLowerCase()}`}
                  >
                    <preset.icon className="w-3 h-3" />
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Save / Cancel */}
          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <Button variant="ghost" onClick={onClose} data-testid="crop-cancel-btn">
              <X className="w-4 h-4 mr-1.5" />Cancel
            </Button>
            <Button onClick={handleSave} data-testid="crop-save-btn">
              <Check className="w-4 h-4 mr-1.5" />Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
